import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faTimes, faUser, faCalendar, 
    faWeight, faIdCard, faFileContract, faRobot 
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../utils/axiosConfig';
import { dashboardService } from '../services/dashboardService';
import { useFlashMessage } from './FlashMessage';
import { getErrorMessage } from '../utils/errorMessages';
import { getSubtypeLabel } from '../data/productTaxonomy';
import './StockModule.css';
import './StockItemList.css';

const ContractDetailsModal = ({ isOpen, onClose, analysisId }) => {
    const [analysis, setAnalysis] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [employeeInfo, setEmployeeInfo] = useState(null);
    const [loadingEmployee, setLoadingEmployee] = useState(false);
    const [currentUserInfo, setCurrentUserInfo] = useState(null);
    const { showSuccess, showError } = useFlashMessage();

    useEffect(() => {
        if (isOpen && analysisId) {
            // Clear previous data when loading new contract details
            setAnalysis(null);
            setEmployeeInfo(null);
            setError(null);
            loadContractDetails();
        } else if (!isOpen) {
            // Clear state when modal is closed
            setAnalysis(null);
            setEmployeeInfo(null);
            setError(null);
            setLoading(false);
            setLoadingEmployee(false);
        }
    }, [isOpen, analysisId]);

    // Load current user info for employee lookup
    useEffect(() => {
        const loadCurrentUserInfo = async () => {
            try {
                const userInfo = await dashboardService.getUserInfo();
                setCurrentUserInfo(userInfo);
            } catch (error) {
                console.error('Error loading current user info:', error);
            }
        };
        loadCurrentUserInfo();
    }, []);

    // Function to get employee info by Django user ID (same as CRM Banking)
    const getEmployeeInfoByUserId = async (djangoUserId) => {
        if (!djangoUserId) return null;
        
        try {
            console.log(`ContractDetailsModal: Looking up employee info for Django user ID: ${djangoUserId}`);
            console.log(`ContractDetailsModal: Current user info:`, currentUserInfo);
            console.log(`ContractDetailsModal: Comparing currentUserInfo.user_id (${currentUserInfo?.user_id}) with parseInt(djangoUserId) (${parseInt(djangoUserId)})`);
            
            // For now, we'll use the Dashboard API to get user info by the current user
            // In a future enhancement, we could create a backend endpoint to lookup any user by ID
            // But for documents uploaded by the current user, we can use their own info
            if (currentUserInfo && (
                currentUserInfo.user_id === parseInt(djangoUserId) || 
                String(currentUserInfo.user_id) === String(djangoUserId) ||
                parseInt(currentUserInfo.user_id) === parseInt(djangoUserId)
            )) {
                const employeeInfo = {
                    first_name: currentUserInfo.first_name,
                    last_name: currentUserInfo.last_name,
                    position: currentUserInfo.position,
                    department: currentUserInfo.department,
                    office: currentUserInfo.office,
                    employee_id: currentUserInfo.employee_id
                };
                
                console.log(`ContractDetailsModal: Found employee info for user ${djangoUserId}:`, employeeInfo);
                return employeeInfo;
            } else {
                // If we have current user info and the user ID doesn't match, 
                // it might still be the same user due to data inconsistencies
                // Let's use current user info as fallback if available
                if (currentUserInfo && currentUserInfo.first_name && currentUserInfo.last_name) {
                    console.log(`ContractDetailsModal: User ID mismatch, but using current user info as fallback`);
                    const fallbackInfo = {
                        first_name: currentUserInfo.first_name,
                        last_name: currentUserInfo.last_name,
                        position: currentUserInfo.position || 'Unknown',
                        department: currentUserInfo.department || 'Unknown',
                        office: currentUserInfo.office || 'Unknown',
                        employee_id: currentUserInfo.employee_id || djangoUserId
                    };
                    return fallbackInfo;
                }
                
                // For other users, we'd need a backend endpoint to fetch user info by ID
                // For now, return basic info indicating we can't fetch other users' data
                const placeholderInfo = {
                    first_name: 'User',
                    last_name: `ID ${djangoUserId}`,
                    position: 'Unknown',
                    department: 'Unknown',
                    office: 'Unknown',
                    employee_id: djangoUserId
                };
                
                console.log(`ContractDetailsModal: Cannot fetch info for other user ID ${djangoUserId}, using placeholder`);
                return placeholderInfo;
            }
        } catch (error) {
            console.error(`ContractDetailsModal: Error fetching employee info for user ID ${djangoUserId}:`, error);
            return null;
        }
    };

    const loadContractDetails = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await axiosInstance.get(`/api/stock/contracts/analysis/${analysisId}/`);
            setAnalysis(response.data);
            
            // Load employee information if we have a created_by user
            if (response.data.created_by) {
                console.log(`ContractDetailsModal: Contract created_by user ID: ${response.data.created_by}`);
                setLoadingEmployee(true);
                try {
                    const empInfo = await getEmployeeInfoByUserId(response.data.created_by);
                    console.log(`ContractDetailsModal: Retrieved employee info:`, empInfo);
                    setEmployeeInfo(empInfo);
                } catch (error) {
                    console.error('Error fetching employee info for contract:', error);
                } finally {
                    setLoadingEmployee(false);
                }
            }
        } catch (err) {
            console.error('Error loading contract details:', err);
            setError('Failed to load contract details');
        } finally {
            setLoading(false);
        }
    };

    // Download contract document (same pattern as CRM Banking)
    const downloadDocument = async (documentId, filename) => {
        try {
            const response = await axiosInstance.get(`/api/stock/contracts/download/${documentId}/`, {
                responseType: 'blob'
            });
            
            // Determine file extension from filename or content type
            let extension = '';
            if (filename && filename.includes('.')) {
                extension = '.' + filename.split('.').pop();
            } else if (response.headers['content-type']) {
                const contentType = response.headers['content-type'];
                if (contentType.includes('pdf')) extension = '.pdf';
                else if (contentType.includes('word')) extension = '.docx';
                else if (contentType.includes('excel')) extension = '.xlsx';
            }
            
            // Create download filename
            const downloadFilename = filename || `Contract_Document_${documentId}${extension}`;
            
            // Create blob URL and trigger download
            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = downloadFilename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            console.log(`Downloaded contract ${documentId} as "${downloadFilename}"`);
            showSuccess(`Document "${downloadFilename}" downloaded successfully`);
            
        } catch (error) {
            console.error('Error downloading contract:', error);
            showError(getErrorMessage(error, 'Contract operation failed. Please try again.'));
        }
    };

    // Delete contract document (same pattern as CRM Banking)
    const deleteDocument = async (documentId, filename) => {
        if (!window.confirm(`Are you sure you want to delete "${filename}"? This action cannot be undone.`)) {
            return;
        }
        
        try {
            await axiosInstance.delete(`/api/stock/contracts/delete/${documentId}/`);
            
            // Show success message
            showSuccess(`Document "${filename}" deleted successfully`);
            
            // Close modal since document no longer exists
            onClose();
            
        } catch (error) {
            console.error('Error deleting contract:', error);
            showError(getErrorMessage(error, 'Contract operation failed. Please try again.'));
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString();
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return 'N/A';
        if (bytes < 1024) return `${bytes} Bytes`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const formatFieldValue = (value, fieldType = null) => {
        if (!value || value === null || value === undefined) {
            return <span className="no-value-text">Not found</span>;
        }

        // Format value based on field type
        let displayValue = value;
        if (fieldType === 'product_type') {
            displayValue = getSubtypeLabel(value) || value;
        } else if (fieldType === 'source_location') {
            displayValue = value;
        }

        return <span className="value-text">{displayValue}</span>;
    };

    if (!isOpen) return null;

    const modalContent = (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content contract-details-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>
                        <FontAwesomeIcon icon={faFileContract} className="header-icon" />
                        Contract Details
                    </h2>
                    <button type="button" className="close-button" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="modal-body">
                    {loading && (
                        <div className="loading-state">
                            <div className="spinner"></div>
                            <p>Loading contract details...</p>
                        </div>
                    )}

                    {error && (
                        <div className="error-state">
                            <p className="error-message">{error}</p>
                        </div>
                    )}

                    {analysis && (
                        <div className="contract-details">
                            {/* Debug logging */}
                            {console.log('🔍 CONTRACT ANALYSIS DATA:', analysis)}
                            {console.log('🔍 DOCUWARE DOCUMENT ID:', analysis.docuware_document_id)}
                            
                            {/* Contract Metadata */}
                            <div className="details-section">
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'start',
                                    padding: '0.75rem',
                                    marginBottom: '0.5rem',
                                    backgroundColor: '#ffffff',
                                    borderRadius: '6px',
                                    border: '1px solid #e5e7eb'
                                }}>
                                    <div style={{ flex: 1 }}>
                                        <div className="document-header">
                                            <div className="item-name-unified">
                                                {analysis.original_filename || 'Unknown File'}
                                            </div>
                                        </div>
                                        <div className="document-metadata">
                                            <span>Uploaded: {formatDate(analysis.created_at)}</span>
                                            {analysis.file_size && (
                                                <span> | Size: {formatFileSize(analysis.file_size)}</span>
                                            )}
                                        </div>
                                        
                                        {/* Employee Information Section - Matching Banking File Format */}
                                        <div style={{ 
                                            fontSize: '0.85rem', 
                                            color: '#495057',
                                            marginTop: '0.5rem',
                                            padding: '0.4rem 0.6rem',
                                            backgroundColor: '#ffffff',
                                            borderRadius: '4px',
                                            border: '1px solid #dee2e6'
                                        }}>
                                            <strong>Uploaded by:</strong>
                                            {loadingEmployee ? (
                                                <span style={{ marginLeft: '0.5rem', fontStyle: 'italic' }}>Loading...</span>
                                            ) : employeeInfo ? (
                                                <div style={{ marginTop: '0.25rem' }}>
                                                    <div><strong>{employeeInfo.first_name} {employeeInfo.last_name}</strong></div>
                                                    {employeeInfo.position && (
                                                        <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                                                            {employeeInfo.position}
                                                            {employeeInfo.department && ` • ${employeeInfo.department}`}
                                                            {employeeInfo.office && ` • ${employeeInfo.office}`}
                                                        </div>
                                                    )}
                                                    <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                                                        Employee ID: {employeeInfo.employee_id}
                                                    </div>
                                                </div>
                                            ) : (
                                                <span style={{ marginLeft: '0.5rem', fontStyle: 'italic', color: '#6c757d' }}>
                                                    Employee info not available
                                                </span>
                                            )}
                                        </div>
                                        
                                        {/* Document ID - Matching Banking File Format */}
                                        <div style={{ 
                                            fontSize: '0.8rem', 
                                            color: '#6c757d',
                                            marginTop: '0.25rem'
                                        }}>
                                            Document ID: {analysis.docuware_document_id || 'Not available'}
                                        </div>
                                    </div>
                                    
                                    {/* Download Button */}
                                    <div style={{ display: 'flex', gap: '0.5rem', marginLeft: '1rem' }}>
                                        <button
                                            onClick={() => {
                                                console.log('🔽 Download clicked, document_id:', analysis.docuware_document_id);
                                                if (analysis.docuware_document_id) {
                                                    downloadDocument(analysis.docuware_document_id, analysis.original_filename);
                                                } else {
                                                    alert('No DocuWare document ID available');
                                                }
                                            }}
                                            style={{
                                                background: 'linear-gradient(135deg, #4a6b25, #3a5b15)',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '6px',
                                                padding: '0.4rem 0.8rem',
                                                fontSize: '0.8rem',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                minWidth: '80px',
                                                transition: 'all 0.2s',
                                                boxShadow: '0 2px 4px rgba(74, 107, 37, 0.15)'
                                            }}
                                        >
                                            Download
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Analysis Information */}
                            <div className="details-section">
                                {analysis.extraction_guidance && (
                                    <div className="guidance-info">
                                        <strong>User Guidance:</strong> "{analysis.extraction_guidance}"
                                    </div>
                                )}

                                {/* Extracted Data */}
                                {analysis.extracted_data && (
                                    <div className="extracted-data">
                                        <h4>Extracted Contract Information</h4>
                                        
                                        <div className="extraction-fields-grid">
                                            <div className="extraction-field-card">
                                                <div className="field-content">
                                                    <div className="field-label">Product Type</div>
                                                    <div className="field-value">
                                                        {formatFieldValue(analysis.extracted_data.product_type, 'product_type')}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="extraction-field-card">
                                                <div className="field-content">
                                                    <div className="field-label">Country of Origin</div>
                                                    <div className="field-value">
                                                        {formatFieldValue(analysis.extracted_data.country_of_origin)}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="extraction-field-card">
                                                <div className="field-content">
                                                    <div className="field-label">Supplier</div>
                                                    <div className="field-value">
                                                        {formatFieldValue(analysis.extracted_data.supplier)}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="extraction-field-card">
                                                <div className="field-content">
                                                    <div className="field-label">Source Location</div>
                                                    <div className="field-value">
                                                        {formatFieldValue(analysis.extracted_data.source_location, 'source_location')}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="extraction-field-card">
                                                <div className="field-content">
                                                    <div className="field-label">Quality Specifications</div>
                                                    <div className="field-value">
                                                        {formatFieldValue(analysis.extracted_data.quality_specifications)}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="extraction-field-card">
                                                <div className="field-content">
                                                    <div className="field-label">Quantity</div>
                                                    <div className="field-value">
                                                        {formatFieldValue(analysis.extracted_data.quantity)}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="extraction-field-card">
                                                <div className="field-content">
                                                    <div className="field-label">Size Specifications</div>
                                                    <div className="field-value">
                                                        {formatFieldValue(analysis.extracted_data.size_specifications)}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="extraction-field-card">
                                                <div className="field-content">
                                                    <div className="field-label">Purchase Price</div>
                                                    <div className="field-value">
                                                        {formatFieldValue(analysis.extracted_data.purchase_price)}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="extraction-field-card">
                                                <div className="field-content">
                                                    <div className="field-label">Sale Price</div>
                                                    <div className="field-value">
                                                        {formatFieldValue(analysis.extracted_data.sale_price)}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {analysis.error_message && (
                                    <div className="error-section">
                                        <h4>Analysis Error</h4>
                                        <p className="error-message">{analysis.error_message}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    // Render modal using Portal to document.body to escape container boundaries
    return ReactDOM.createPortal(modalContent, document.body);
};

export default ContractDetailsModal; 